import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack, useTheme} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {PuzzlePieceLine} from '@dropbox/dig-icons/assets';
import type {Workstream} from 'client';
import {ClientInstallSteps} from 'components/mcp/ClientInstallSteps';
import {type ClientGuide, EXTERNAL_CLIENT_GUIDES} from 'pages/mcpClientGuides';

/** Theme-aware brand logo for an MCP client, falling back to a generic icon. */
export const ClientLogo = ({guide, large = false}: {guide: ClientGuide; large?: boolean}) => {
  const {mode} = useTheme();
  const isDark = mode === 'dark';
  const size = large ? 40 : 20;
  if (guide.logo) {
    // These brand marks fill their viewBox edge-to-edge. In the chip row we
    // center a smaller logo inside the 20px box so it optically matches the
    // padded Dash-app logos; the large (setup page) variant stays full-bleed.
    const logoPx = large ? size : 14;
    return (
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        style={{width: size, height: size}}
      >
        <img
          src={isDark ? guide.logo.dark : guide.logo.light}
          alt=""
          style={{width: logoPx, height: logoPx, display: 'block'}}
        />
      </Box>
    );
  }
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      style={{width: size, height: size, fontSize: size}}
    >
      <UIIcon src={guide.icon ?? PuzzlePieceLine} size={large ? 'large' : 'standard'} />
    </Box>
  );
};

/**
 * Full-panel "connected" state shown after an MCP app is set up (and on return
 * visits). Shows install steps as reference; reconfiguring is done via the gear
 * icon in the "AI Assistant" accordion header.
 */
export const McpConnectedView = ({
  connectedClientIds,
}: {
  workstream: Workstream;
  connectedClientIds: string[];
}) => {
  const primary = connectedClientIds
    .map((id) => EXTERNAL_CLIENT_GUIDES.find((g) => g.id === id))
    .filter((g): g is ClientGuide => Boolean(g))[0];

  if (!primary) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      padding="24"
      style={{flex: '1 1 0%', minHeight: 0, overflowY: 'auto'}}
    >
      <Stack gap="16">
        <Box display="flex" alignItems="center" style={{gap: 10}}>
          <ClientLogo guide={primary} />
          <Text isBold size="large">
            {primary.name}
          </Text>
        </Box>
        <ClientInstallSteps guide={primary} />
      </Stack>
    </Box>
  );
};
